import type { ReactNode } from 'react';

import { cn } from '@/utils/cls';

type Tone = 'brand' | 'success' | 'warn' | 'danger' | 'neutral';

interface PillProps {
  tone?: Tone;
  children: ReactNode;
  className?: string;
}

const TONE: Record<Tone, string> = {
  brand: 'bg-brand-500/10 text-brand-700 dark:text-brand-300',
  success: 'bg-success/10 text-success',
  warn: 'bg-warn/15 text-warn',
  danger: 'bg-danger/10 text-danger',
  neutral: 'bg-surface-2 text-ink-soft border border-line',
};

/** Tiny status chip — used in tables for bill status, stock, etc. */
export function Pill({ tone = 'neutral', className, children }: PillProps) {
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full whitespace-nowrap',
        TONE[tone],
        className,
      )}
    >
      {children}
    </span>
  );
}

const STATUS_TONE: Record<string, Tone> = {
  paid: 'success',
  partial: 'warn',
  partial_paid: 'warn',
  due: 'danger',
  overdue: 'danger',
  cancelled: 'neutral',
};

const STATUS_LABEL: Record<string, string> = {
  paid: 'Paid',
  partial: 'Partial',
  partial_paid: 'Partial',
  due: 'Due',
  overdue: 'Overdue',
  cancelled: 'Cancelled',
};

export function StatusPill({ status }: { status?: string | null }) {
  const key = (status ?? 'due').toLowerCase();
  return <Pill tone={STATUS_TONE[key] ?? 'neutral'}>{STATUS_LABEL[key] ?? status}</Pill>;
}
